/**
 * Game Storage Module
 * Centralizes all localStorage operations for the Subletters game
 */

const STORAGE_KEY = 'subletters-game-data'

// ============================================================================
// Core Read/Write Operations
// ============================================================================

/**
 * Get the entire game data object from localStorage
 * @returns {Object} The parsed game data or empty object
 */
export const getGameData = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}
  } catch {
    return {}
  }
}

/**
 * Save the entire game data object to localStorage
 * @param {Object} data - The game data to save
 */
export const saveGameData = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // Storage might be full or unavailable
  }
}

/**
 * Get data for a specific puzzle index
 * @param {number} puzzleIndex - The puzzle index
 * @returns {Object} The puzzle data or empty object
 */
export const getPuzzleData = (puzzleIndex) => {
  const data = getGameData()
  return data[puzzleIndex.toString()] || {}
}

/**
 * Get data for a specific level within a puzzle
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number (1-4)
 * @returns {Object} The level data or empty object
 */
export const getLevelData = (puzzleIndex, level) => {
  const puzzleData = getPuzzleData(puzzleIndex)
  const levelData = puzzleData[level]
  if (levelData && typeof levelData === 'object') {
    return levelData
  }
  return {}
}

// ============================================================================
// Global Settings
// ============================================================================

/**
 * Check if the user has played before
 * @returns {boolean}
 */
export const hasPlayed = () => {
  const data = getGameData()
  return data.played === true
}

/**
 * Mark that the user has played
 */
export const markAsPlayed = () => {
  const data = getGameData()
  data.played = true
  saveGameData(data)
}

/**
 * Check if the user has seen the keyboard help message
 * @returns {boolean}
 */
export const hasSeenKeyboardHelp = () => {
  const data = getGameData()
  return data.seenKeyboardHelp === true
}

/**
 * Mark that the user has seen the keyboard help message
 */
export const markKeyboardHelpSeen = () => {
  const data = getGameData()
  data.seenKeyboardHelp = true
  saveGameData(data)
}

export const hasSeenAnnouncement = (id) => {
  const data = getGameData()
  return !!(data.seenAnnouncements && data.seenAnnouncements[id])
}

export const markAnnouncementSeen = (id) => {
  const data = getGameData()
  if (!data.seenAnnouncements) data.seenAnnouncements = {}
  data.seenAnnouncements[id] = true
  saveGameData(data)
}

/**
 * Get the stored version
 * @returns {string|undefined}
 */
export const getStoredVersion = () => {
  const data = getGameData()
  return data.version
}

/**
 * Initialize storage for a new user or version update
 * @param {string} version - The current game version
 * @param {boolean} preservePlayed - Whether to preserve the played flag
 */
export const initializeStorage = (version, preservePlayed = false) => {
  const data = getGameData()
  const newData = {
    played: preservePlayed ? data.played : false,
    version: version
  }
  saveGameData(newData)
}

// ============================================================================
// Score & Path Management
// ============================================================================

/**
 * Get the best score for a level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @returns {number|undefined} The best score or undefined if not set
 */
export const getBestScore = (puzzleIndex, level) => {
  const levelData = getLevelData(puzzleIndex, level)
  return levelData.score
}

/**
 * Get the saved path for a level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @returns {string[]} The saved path or empty array
 */
export const getSavedPath = (puzzleIndex, level) => {
  const levelData = getLevelData(puzzleIndex, level)
  return levelData.path || []
}

/**
 * Save a new best score and path
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @param {number} score - The score
 * @param {string[]} path - The path taken
 * @param {Array} hintedIndices - Array of [wordIndex, letterIndex] pairs for hinted words
 * @param {Array} boostedIndices - Array of indices for boosted words
 */
export const saveScore = (puzzleIndex, level, score, path, hintedIndices = [], boostedIndices = []) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (!data[key]) {
    data[key] = {}
  }
  if (!data[key][level] || typeof data[key][level] !== 'object') {
    data[key][level] = {}
  }

  data[key][level].score = score
  data[key][level].path = path
  data[key][level].hintedIndices = hintedIndices
  data[key][level].boostedIndices = boostedIndices

  // Ensure challenge object exists for next level unlock tracking
  if (!data[key].challenge) {
    data[key].challenge = {}
  }
  if (level < 4 && !data[key].challenge[level + 1]) {
    data[key].challenge[level + 1] = false
  }

  saveGameData(data)
}

/**
 * Get the saved hinted indices for a level's best path
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @returns {Array} Array of [wordIndex, letterIndex] pairs
 */
export const getSavedHintedIndices = (puzzleIndex, level) => {
  const levelData = getLevelData(puzzleIndex, level)
  return levelData.hintedIndices || []
}

/**
 * Get the saved boosted indices for a level's best path
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @returns {Array} Array of boosted word indices
 */
export const getSavedBoostedIndices = (puzzleIndex, level) => {
  const levelData = getLevelData(puzzleIndex, level)
  return levelData.boostedIndices || []
}

// ============================================================================
// Boosts Management
// ============================================================================

/**
 * Get the number of boosts used for a specific level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @returns {number} Number of boosts used
 */
export const getBoostsUsedForLevel = (puzzleIndex, level) => {
  const levelData = getLevelData(puzzleIndex, level)
  return levelData.boosts || 0
}

/**
 * Get total boosts remaining for a puzzle (4 max per day)
 * @param {number} puzzleIndex - The puzzle index
 * @returns {number} Boosts remaining (0-4)
 */
export const getBoostsRemaining = (puzzleIndex) => {
  const puzzleData = getPuzzleData(puzzleIndex)
  let totalUsed = 0

  for (const levelKey in puzzleData) {
    if (levelKey !== 'challenge' && levelKey !== 'solutionsViewed' && levelKey !== 'solutionsRevealed') {
      const boostsUsed = puzzleData[levelKey]?.boosts ?? 0
      totalUsed += boostsUsed
    }
  }

  return Math.max(4 - totalUsed, 0)
}

/**
 * Increment boosts used for a level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 */
export const incrementBoostsUsed = (puzzleIndex, level) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (!data[key]) data[key] = {}
  if (!data[key][level] || typeof data[key][level] !== 'object') {
    data[key][level] = {}
  }

  const existingBoosts = data[key][level].boosts || 0
  data[key][level].boosts = existingBoosts + 1

  saveGameData(data)
}

// ============================================================================
// Hints Management
// ============================================================================

/**
 * Get the number of hints used for a specific level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @returns {number} Number of hints used
 */
export const getHintsUsedForLevel = (puzzleIndex, level) => {
  const levelData = getLevelData(puzzleIndex, level)
  return levelData.hints || 0
}

/**
 * Get total hints used across all levels for a puzzle
 * @param {number} puzzleIndex - The puzzle index
 * @returns {number} Total hints used
 */
export const getTotalHintsUsed = (puzzleIndex) => {
  const puzzleData = getPuzzleData(puzzleIndex)
  let totalHints = 0

  for (const levelKey in puzzleData) {
    if (levelKey !== 'challenge' && levelKey !== 'solutionsViewed' && levelKey !== 'solutionsRevealed') {
      const hints = puzzleData[levelKey]?.hints
      if (typeof hints === 'number') {
        totalHints += hints
      }
    }
  }

  return totalHints
}

/**
 * Increment hints used for a level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 */
export const incrementHintsUsed = (puzzleIndex, level) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (!data[key]) data[key] = {}
  if (!data[key][level] || typeof data[key][level] !== 'object') {
    data[key][level] = {}
  }

  const existingHints = data[key][level].hints || 0
  data[key][level].hints = existingHints + 1

  saveGameData(data)
}

// ============================================================================
// First Transformation Flag
// ============================================================================

/**
 * Check if first transformation has been made for a level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @returns {boolean}
 */
export const hasFirstTransformation = (puzzleIndex, level) => {
  const levelData = getLevelData(puzzleIndex, level)
  return levelData.hasMadeFirstTransformation === true
}

/**
 * Set the first transformation flag for a level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @param {boolean} value - The flag value
 */
export const setFirstTransformation = (puzzleIndex, level, value) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (!data[key]) data[key] = {}
  if (!data[key][level] || typeof data[key][level] !== 'object') {
    data[key][level] = {}
  }

  data[key][level].hasMadeFirstTransformation = value
  saveGameData(data)
}

// ============================================================================
// Level Unlock (Challenge) Management
// ============================================================================

/**
 * Check if a level is unlocked
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @returns {boolean}
 */
export const isLevelUnlocked = (puzzleIndex, level) => {
  const puzzleData = getPuzzleData(puzzleIndex)
  return puzzleData.challenge?.[level] === true
}

/**
 * Unlock a level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 */
export const unlockLevel = (puzzleIndex, level) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (!data[key]) data[key] = {}
  if (!data[key].challenge) data[key].challenge = {}

  data[key].challenge[level] = true
  saveGameData(data)
}

/**
 * Get the highest unlocked level for a puzzle
 * @param {number} puzzleIndex - The puzzle index
 * @returns {number} Highest unlocked level (1-4)
 */
export const getHighestUnlockedLevel = (puzzleIndex) => {
  const puzzleData = getPuzzleData(puzzleIndex)
  const challenge = puzzleData.challenge || {}

  if (challenge[5]) return 5
  if (challenge[4]) return 4
  if (challenge[3]) return 3
  if (challenge[2]) return 2
  return 1
}

// ============================================================================
// Solutions Viewing
// ============================================================================

/**
 * Check if solutions have been viewed for a puzzle
 * @param {number} puzzleIndex - The puzzle index
 * @returns {boolean}
 */
export const areSolutionsViewed = (puzzleIndex) => {
  const puzzleData = getPuzzleData(puzzleIndex)
  return puzzleData.solutionsViewed === true
}

/**
 * Mark solutions as viewed for a puzzle
 * @param {number} puzzleIndex - The puzzle index
 */
export const markSolutionsViewed = (puzzleIndex) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (!data[key]) data[key] = {}
  data[key].solutionsViewed = true

  saveGameData(data)
}

/**
 * Check if solutions have been revealed for a puzzle
 * @param {number} puzzleIndex - The puzzle index
 * @returns {boolean}
 */
export const areSolutionsRevealed = (puzzleIndex) => {
  const puzzleData = getPuzzleData(puzzleIndex)
  return puzzleData.solutionsRevealed === true
}

/**
 * Mark solutions as revealed for a puzzle
 * @param {number} puzzleIndex - The puzzle index
 */
export const markSolutionsRevealed = (puzzleIndex) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (!data[key]) data[key] = {}
  data[key].solutionsRevealed = true

  saveGameData(data)
}

// ============================================================================
// Solve Status Helpers
// ============================================================================

/**
 * Check if a level is solved (and if optimally)
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @param {number} optimalScore - The optimal score for this level
 * @returns {{solved: boolean, solved_optimally: boolean}}
 */
export const getSolveStatus = (puzzleIndex, level, optimalScore) => {
  const score = getBestScore(puzzleIndex, level)

  if (score !== undefined && score > 0) {
    return {
      solved: true,
      solved_optimally: score === optimalScore
    }
  }

  return { solved: false, solved_optimally: false }
}

/**
 * Get all revealed puzzle indices (for loading state)
 * @returns {Object} Map of puzzleIndex -> true for revealed puzzles
 */
export const getAllRevealedPuzzles = () => {
  const data = getGameData()
  const revealed = {}

  for (const key of Object.keys(data)) {
    if (data[key]?.solutionsRevealed) {
      revealed[key] = true
    }
  }

  return revealed
}

// ============================================================================
// Checkpoint Management (for hint/boost progress preservation)
// ============================================================================

/**
 * Save checkpoint state when hint or boost is used
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @param {Object} checkpoint - The checkpoint data
 */
export const saveCheckpoint = (puzzleIndex, level, checkpoint) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (!data[key]) data[key] = {}
  if (!data[key][level] || typeof data[key][level] !== 'object') {
    data[key][level] = {}
  }

  data[key][level].checkpoint = checkpoint
  saveGameData(data)
}

/**
 * Get checkpoint state for a level
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 * @returns {Object|null} The checkpoint data or null
 */
export const getCheckpoint = (puzzleIndex, level) => {
  const levelData = getLevelData(puzzleIndex, level)
  return levelData.checkpoint || null
}

/**
 * Find the first level that has a checkpoint for a puzzle
 * @param {number} puzzleIndex - The puzzle index
 * @returns {number|null} The level number with a checkpoint, or null if none
 */
export const findLevelWithCheckpoint = (puzzleIndex) => {
  const puzzleData = getPuzzleData(puzzleIndex)
  for (let level = 1; level <= 5; level++) {
    if (puzzleData[level]?.checkpoint) {
      return level
    }
  }
  return null
}

/**
 * Clear checkpoint for a level (when level is completed or reset)
 * @param {number} puzzleIndex - The puzzle index
 * @param {number} level - The level number
 */
export const clearCheckpoint = (puzzleIndex, level) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (data[key] && data[key][level]) {
    delete data[key][level].checkpoint
    saveGameData(data)
  }
}

// ============================================================================
// Tutorial Completion
// ============================================================================

/**
 * Check if the tutorial has been completed
 * @returns {boolean}
 */
export const hasTutorialCompleted = () => {
  const data = getGameData()
  return data.tutorialCompleted === true
}

/**
 * Mark the tutorial as completed
 */
export const markTutorialCompleted = () => {
  const data = getGameData()
  data.tutorialCompleted = true
  saveGameData(data)
}

/**
 * Clear all level data for a puzzle index (used to reset tutorial puzzle)
 * @param {number} puzzleIndex - The puzzle index to clear
 */
export const saveLastActiveLevel = (puzzleIndex, level) => {
  const data = getGameData()
  const key = puzzleIndex.toString()
  if (!data[key]) data[key] = {}
  data[key].lastLevel = level
  saveGameData(data)
}

export const getLastActiveLevel = (puzzleIndex) => {
  const puzzleData = getPuzzleData(puzzleIndex)
  return puzzleData.lastLevel || 1
}

export const clearPuzzleData = (puzzleIndex) => {
  const data = getGameData()
  const key = puzzleIndex.toString()

  if (data[key]) {
    delete data[key]
    saveGameData(data)
  }
}
